import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader2, MessageSquare, Bug, FileQuestion, Inbox, Filter } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_PILLS = {
  open: { label: "Open", className: "bg-blue-500/15 text-blue-600 border-blue-500/30" },
  in_progress: { label: "In progress", className: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
  resolved: { label: "Resolved", className: "bg-primary/15 text-primary border-primary/30" },
  closed: { label: "Closed", className: "bg-muted text-muted-foreground border-border" },
};

const CAT_ICON = { bug: Bug, content: FileQuestion, feedback: MessageSquare };

const formatDateTime = (iso) => iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const AdminSupportPage = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/admin/support/tickets`, { params: { status, category, unread_only: unreadOnly } }),
        axios.get(`${API}/admin/support/stats`),
      ]);
      setTickets(tRes.data.tickets || []);
      setStats(sRes.data);
    } catch {
      toast.error("Could not load support tickets");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status, category, unreadOnly]);

  return (
    <div className="space-y-6" data-testid="admin-support-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Support inbox</h1>
        <p className="text-muted-foreground mt-1">Student bug reports, content issues and feedback.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <StatBox label="Unread" value={stats.admin_unread} testId="stat-admin-unread" accent={stats.admin_unread > 0} />
          <StatBox label="Open tickets" value={stats.total_open} testId="stat-total-open" />
          <StatBox label="Bug reports open" value={stats.bug_count} testId="stat-bug-count" />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-secondary/50 border border-border">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <FilterChip label="All" active={status === "all"} onClick={() => setStatus("all")} testId="filter-status-all" />
        {Object.entries(STATUS_PILLS).map(([k, p]) => (
          <FilterChip key={k} label={p.label} active={status === k} onClick={() => setStatus(k)} testId={`filter-status-${k}`} />
        ))}
        <span className="w-px h-5 bg-border mx-1" />
        <FilterChip label="All categories" active={category === "all"} onClick={() => setCategory("all")} testId="filter-cat-all" />
        <FilterChip label="Bug" active={category === "bug"} onClick={() => setCategory("bug")} testId="filter-cat-bug" />
        <FilterChip label="Content" active={category === "content"} onClick={() => setCategory("content")} testId="filter-cat-content" />
        <FilterChip label="Feedback" active={category === "feedback"} onClick={() => setCategory("feedback")} testId="filter-cat-feedback" />
        <span className="w-px h-5 bg-border mx-1" />
        <FilterChip label="Unread only" active={unreadOnly} onClick={() => setUnreadOnly(v => !v)} testId="filter-unread" />
      </div>

      {/* Table */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        {loading ? (
          <div className="p-12 flex items-center justify-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : tickets.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground text-sm">
            <Inbox className="w-10 h-10 mx-auto mb-3 opacity-40" />
            No tickets match these filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-muted-foreground border-b border-border">
                <tr>
                  <th className="text-left py-3 px-4">Subject</th>
                  <th className="text-left py-3 px-4">From</th>
                  <th className="text-left py-3 px-4">Category</th>
                  <th className="text-left py-3 px-4">Status</th>
                  <th className="text-left py-3 px-4">Updated</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map((t) => {
                  const Icon = CAT_ICON[t.category] || MessageSquare;
                  const pill = STATUS_PILLS[t.status] || STATUS_PILLS.open;
                  return (
                    <tr key={t.id} className={`border-b border-border/50 hover:bg-secondary/40 cursor-pointer transition-colors ${t.admin_unread ? "bg-primary/5" : ""}`}>
                      <td className="py-3 px-4">
                        <Link to={`/admin/support/${t.id}`} className="font-medium hover:underline" data-testid={`ticket-link-${t.id}`}>
                          {t.admin_unread && <span className="inline-block w-2 h-2 rounded-full bg-primary mr-2" aria-label="Unread" />}
                          {t.subject}
                        </Link>
                        {t.page_url && <p className="text-xs text-muted-foreground mt-0.5">{t.page_url}</p>}
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-sm">{t.user_name}</p>
                        <p className="text-xs text-muted-foreground">{t.user_email}</p>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                          <Icon className="w-3 h-3" /> {t.category}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${pill.className}`}>{pill.label}</span>
                      </td>
                      <td className="py-3 px-4 text-xs text-muted-foreground whitespace-nowrap">{formatDateTime(t.updated_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

const StatBox = ({ label, value, testId, accent }) => (
  <div data-testid={testId} className={`rounded-xl border px-4 py-3 ${accent ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${accent ? "text-primary" : "text-foreground"}`}>{value}</p>
  </div>
);

const FilterChip = ({ label, active, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`text-xs px-3 py-1 rounded-full border transition-colors ${
      active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border text-muted-foreground hover:text-foreground"
    }`}
  >
    {label}
  </button>
);

export default AdminSupportPage;
